/**
 * Admin-only Service Accounts management.
 * Id + secret are auto-generated; secret rotates every 15 days (or on demand).
 */

import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
} from '@backstage/core-components';
import {
  configApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAdminGroupMember } from '../devtools/AdminGroupMemberContext';

type BelongsToCheck = 'idle' | 'checking' | 'found' | 'missing' | 'error';

const SA_PERMISSION_FLAGS = [
  'readUser',
  'readGroups',
  'readComponents',
  'readTemplates',
  'readTechdocs',
  'readTickets',
  'createTickets',
] as const;

type SaPermissionFlag = (typeof SA_PERMISSION_FLAGS)[number];

type SaPermissions = Record<SaPermissionFlag, boolean>;

function emptyPermissions(): SaPermissions {
  return {
    readUser: false,
    readGroups: false,
    readComponents: false,
    readTemplates: false,
    readTechdocs: false,
    readTickets: false,
    createTickets: false,
  };
}

/** Accept API payloads in new flat shape or legacy read/write matrix. */
function normalizePermissions(raw: unknown): SaPermissions {
  const base = emptyPermissions();
  if (!raw || typeof raw !== 'object') return base;
  const obj = raw as Record<string, unknown>;
  for (const flag of SA_PERMISSION_FLAGS) {
    if (flag in obj) base[flag] = obj[flag] === true;
  }
  const read = obj.read;
  const write = obj.write;
  if (read && typeof read === 'object') {
    const r = read as Record<string, unknown>;
    if (r.users === true) base.readUser = true;
    if (r.groups === true) base.readGroups = true;
    if (r.templates === true) base.readTemplates = true;
    if (r.techdocs === true) base.readTechdocs = true;
    if (r.tickets === true) base.readTickets = true;
  }
  if (write && typeof write === 'object') {
    const w = write as Record<string, unknown>;
    if (w.templates === true || w.tickets === true) base.createTickets = true;
  }
  return base;
}

const PERMISSION_LABELS: Record<SaPermissionFlag, string> = {
  readUser: 'Read User',
  readGroups: 'Read Groups',
  readComponents: 'Read Components',
  readTemplates: 'Read Templates',
  readTechdocs: 'Read TechDocs',
  readTickets: 'Read Tickets',
  createTickets: 'Create Tickets',
};

type ServiceAccount = {
  accountId: string;
  id: string;
  displayName: string | null;
  status: 'active' | 'revoked' | 'suspended';
  userEntityRef: string;
  createdBy: string;
  createdAt: string;
  secretRotatedAt: string;
  secretRotationDueAt: string;
  secretRotationDue: boolean;
  hasPendingSecret: boolean;
  revokedAt: string | null;
  readCount: number;
  writeCount: number;
  lastUsedAt: string | null;
  belongsTo: string;
  teamName: string;
  permissions: SaPermissions;
};

function PermissionMatrix(props: {
  value: SaPermissions;
  onChange: (next: SaPermissions) => void;
  disabled?: boolean;
}) {
  const { value, onChange, disabled } = props;
  const toggle = (flag: SaPermissionFlag) => {
    onChange({ ...value, [flag]: !value[flag] });
  };
  const labelStyle: CSSProperties = {
    whiteSpace: 'nowrap',
    minWidth: 140,
  };
  return (
    <Box>
      <Typography variant="body2" color="textSecondary" paragraph>
        Grant only what this account needs. Write is Create Tickets only; users,
        groups, and components cannot be added. Server secrets (including{' '}
        GITLAB_TOKEN) are never available to service-account tokens.
      </Typography>
      <Table size="small">
        <TableBody>
          {SA_PERMISSION_FLAGS.map(flag => (
            <TableRow key={flag}>
              <TableCell style={labelStyle}>{PERMISSION_LABELS[flag]}</TableCell>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  checked={Boolean(value[flag])}
                  disabled={disabled}
                  onChange={() => toggle(flag)}
                  inputProps={{ 'aria-label': PERMISSION_LABELS[flag] }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function formatStatus(status: ServiceAccount['status'] | string): string {
  if (status === 'suspicious') return 'suspended';
  return status;
}

function isSuspended(status: ServiceAccount['status'] | string): boolean {
  return status === 'suspended' || status === 'suspicious';
}

type AuditLogEntry = {
  id: string;
  accessedAt: string;
  method: string;
  path: string;
  kind: 'read' | 'write';
  writeType: 'create' | 'update' | 'delete' | 'other' | null;
  bytesWritten: number | null;
  outcome: 'allowed' | 'denied';
  statusCode: number | null;
};

async function waitForIdentity(identityApi: {
  getBackstageIdentity: () => Promise<unknown>;
}): Promise<void> {
  for (let i = 0; i < 6; i++) {
    try {
      await identityApi.getBackstageIdentity();
      return;
    } catch {
      await new Promise(r => setTimeout(r, 150 * (i + 1)));
    }
  }
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatWriteType(entry: AuditLogEntry): string {
  if (entry.kind !== 'write') return '—';
  return entry.writeType || 'other';
}

export const ServiceAccountsPage = () => {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const adminMember = useAdminGroupMember();
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ServiceAccount[]>([]);
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [belongsTo, setBelongsTo] = useState('');
  const [belongsToCheck, setBelongsToCheck] = useState<BelongsToCheck>('idle');
  const [teamName, setTeamName] = useState('');
  const [permissions, setPermissions] = useState<SaPermissions>(emptyPermissions);
  const [editAccount, setEditAccount] = useState<ServiceAccount | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editBelongsTo, setEditBelongsTo] = useState('');
  const [editBelongsToCheck, setEditBelongsToCheck] =
    useState<BelongsToCheck>('idle');
  const [editTeamName, setEditTeamName] = useState('');
  const [editPermissions, setEditPermissions] =
    useState<SaPermissions>(emptyPermissions);
  const [saving, setSaving] = useState(false);

  const checkBelongsToInIronPhoenix = useCallback(
    async (username: string): Promise<BelongsToCheck> => {
      const normalized = username.trim().toLowerCase();
      if (!normalized) return 'idle';
      try {
        const entity = await catalogApi.getEntityByRef({
          kind: 'User',
          namespace: 'default',
          name: normalized,
        });
        return entity ? 'found' : 'missing';
      } catch {
        // Catalog returns NotFound for missing users in some versions.
        return 'missing';
      }
    },
    [catalogApi],
  );

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (!addOpen) {
      setBelongsToCheck('idle');
    } else {
      const normalized = belongsTo.trim().toLowerCase();
      if (!normalized) {
        setBelongsToCheck('idle');
      } else {
        setBelongsToCheck('checking');
        timer = setTimeout(() => {
          void checkBelongsToInIronPhoenix(normalized).then(result => {
            if (!cancelled) setBelongsToCheck(result);
          });
        }, 350);
      }
    }

    return () => {
      cancelled = true;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [addOpen, belongsTo, checkBelongsToInIronPhoenix]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (!editAccount) {
      setEditBelongsToCheck('idle');
    } else {
      const normalized = editBelongsTo.trim().toLowerCase();
      if (!normalized) {
        setEditBelongsToCheck('idle');
      } else {
        setEditBelongsToCheck('checking');
        timer = setTimeout(() => {
          void checkBelongsToInIronPhoenix(normalized).then(result => {
            if (!cancelled) setEditBelongsToCheck(result);
          });
        }, 350);
      }
    }

    return () => {
      cancelled = true;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [editAccount, editBelongsTo, checkBelongsToInIronPhoenix]);

  const [revealed, setRevealed] = useState<{
    title: string;
    id: string;
    secret: string;
  } | null>(null);

  const [detailAccount, setDetailAccount] = useState<ServiceAccount | null>(
    null,
  );
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    kind: 'revoke' | 'rotate' | 'reinstate';
    account: ServiceAccount;
  } | null>(null);

  const backend = config.getString('backend.baseUrl').replace(/\/$/, '');

  useEffect(() => {
    let cancelled = false;
    waitForIdentity(identityApi).then(() => {
      if (!cancelled) setAuthReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetchApi.fetch(`${backend}/api/service-accounts`, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || `Failed to load (${res.status})`);
      }
      const loaded = (body.items ?? []) as ServiceAccount[];
      setItems(
        loaded.map(item => ({
          ...item,
          permissions: normalizePermissions(item.permissions),
        })),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [backend, fetchApi, identityApi]);

  useEffect(() => {
    if (adminMember === true && authReady) {
      void load();
    }
  }, [adminMember, authReady, load]);

  const withAuth = async (path: string, init?: RequestInit) => {
    const { token } = await identityApi.getCredentials();
    return fetchApi.fetch(`${backend}/api/service-accounts${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers || {}),
      },
    });
  };

  const resetCreateForm = () => {
    setDisplayName('');
    setBelongsTo('');
    setBelongsToCheck('idle');
    setTeamName('');
    setPermissions(emptyPermissions());
  };

  const belongsToHelper = (check: BelongsToCheck): string => {
    if (check === 'checking') {
      return 'Checking Iron Phoenix catalog…';
    }
    if (check === 'found') {
      return 'User found in Iron Phoenix.';
    }
    if (check === 'missing') {
      return 'User not found in Iron Phoenix. They must be a USER_GROUP member synced to the catalog.';
    }
    if (check === 'error') {
      return 'Could not verify user in Iron Phoenix. Try again.';
    }
    return 'Must be an existing GitLab username already in Iron Phoenix.';
  };

  const openEdit = (account: ServiceAccount) => {
    setEditAccount(account);
    setEditDisplayName(account.displayName || '');
    setEditBelongsTo(account.belongsTo || '');
    setEditTeamName(account.teamName || '');
    setEditPermissions(normalizePermissions(account.permissions));
  };

  const onCreate = async () => {
    if (!belongsTo.trim() || !teamName.trim()) {
      setError('Belongs to and Team name are required');
      return;
    }
    const exists = await checkBelongsToInIronPhoenix(belongsTo);
    setBelongsToCheck(exists);
    if (exists !== 'found') {
      setError(
        'Belongs to must be a GitLab username that exists in Iron Phoenix.',
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await withAuth('', {
        method: 'POST',
        body: JSON.stringify({
          displayName: displayName.trim() || undefined,
          belongsTo: belongsTo.trim(),
          teamName: teamName.trim(),
          permissions,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || `Create failed (${res.status})`);
      }
      setAddOpen(false);
      resetCreateForm();
      setRevealed({
        title: 'Service account created',
        id: body.id,
        secret: body.secret,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const onSaveEdit = async () => {
    if (!editAccount) return;
    if (!editBelongsTo.trim() || !editTeamName.trim()) {
      setError('Belongs to and Team name are required');
      return;
    }
    const exists = await checkBelongsToInIronPhoenix(editBelongsTo);
    setEditBelongsToCheck(exists);
    if (exists !== 'found') {
      setError(
        'Belongs to must be a GitLab username that exists in Iron Phoenix.',
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await withAuth(`/${editAccount.accountId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          displayName: editDisplayName.trim() || null,
          belongsTo: editBelongsTo.trim(),
          teamName: editTeamName.trim(),
          permissions: editPermissions,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || `Update failed (${res.status})`);
      }
      setEditAccount(null);
      await load();
      if (detailAccount?.accountId === editAccount.accountId && body.account) {
        setDetailAccount(body.account as ServiceAccount);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const runRevoke = async (account: ServiceAccount) => {
    setError(null);
    try {
      const res = await withAuth(`/${account.accountId}/revoke`, {
        method: 'POST',
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || `Revoke failed (${res.status})`);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const runReinstate = async (account: ServiceAccount) => {
    setError(null);
    try {
      const res = await withAuth(`/${account.accountId}/reinstate`, {
        method: 'POST',
        body: '{}',
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || `Reinstate failed (${res.status})`);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const runRotate = async (account: ServiceAccount) => {
    setError(null);
    try {
      const res = await withAuth(`/${account.accountId}/rotate-secret`, {
        method: 'POST',
        body: '{}',
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || `Rotate failed (${res.status})`);
      }
      setRevealed({
        title: `New secret for ${account.id}`,
        id: body.id ?? account.id,
        secret: body.secret,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onConfirmAction = async () => {
    if (!confirmAction) return;
    const { kind, account } = confirmAction;
    setConfirmAction(null);
    if (kind === 'revoke') {
      await runRevoke(account);
      return;
    }
    if (kind === 'reinstate') {
      await runReinstate(account);
      return;
    }
    await runRotate(account);
  };

  const onRevealPending = async (account: ServiceAccount) => {
    setError(null);
    try {
      const res = await withAuth(`/${account.accountId}/reveal-secret`, {
        method: 'POST',
        body: '{}',
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || `Reveal failed (${res.status})`);
      }
      setRevealed({
        title: `Pending secret for ${account.id}`,
        id: body.id ?? account.id,
        secret: body.secret,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const openAccessLogs = async (account: ServiceAccount) => {
    setDetailAccount(account);
    setAuditLogs([]);
    setAuditError(null);
    setAuditLoading(true);
    try {
      const res = await withAuth(
        `/${account.accountId}/audit-logs?limit=200`,
      );
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || `Failed to load logs (${res.status})`);
      }
      if (body.account) {
        setDetailAccount(body.account as ServiceAccount);
      }
      setAuditLogs(body.items ?? []);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : String(err));
    } finally {
      setAuditLoading(false);
    }
  };

  const searchQuery = search.trim().toLowerCase();
  const filteredItems = searchQuery
    ? items.filter(account => {
        const name = (account.displayName || '').toLowerCase();
        const id = account.id.toLowerCase();
        const owner = (account.belongsTo || '').toLowerCase();
        const team = (account.teamName || '').toLowerCase();
        return (
          name.includes(searchQuery) ||
          id.includes(searchQuery) ||
          owner.includes(searchQuery) ||
          team.includes(searchQuery)
        );
      })
    : items;

  const allowed = adminMember === true;
  const checkingAccess = adminMember === null || !authReady;

  if (checkingAccess) {
    return (
      <Page themeId="tool">
        <Header title="Service Accounts" subtitle="Admin tools" />
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Progress />
          </Box>
        </Content>
      </Page>
    );
  }

  if (!allowed) {
    return (
      <Page themeId="tool">
        <Header title="Service Accounts" subtitle="Admin tools" />
        <Content>
          <Box p={3} maxWidth={560}>
            <Typography variant="body1" paragraph>
              You do not have access to this page. Service Accounts are limited to
              administrators (same group as DevTools / Ticket logs).
            </Typography>
            <Button component={RouterLink} to="/catalog" variant="outlined" color="primary">
              Back to Home
            </Button>
          </Box>
        </Content>
      </Page>
    );
  }

  let accountsBody: JSX.Element;
  if (loading) {
    accountsBody = <Progress />;
  } else if (items.length === 0) {
    accountsBody = (
      <Typography color="textSecondary">
        No service accounts yet. Click Add — id and secret are generated for you.
        CLI: <code>iron-phoenix auth login --service-account</code>
      </Typography>
    );
  } else if (filteredItems.length === 0) {
    accountsBody = (
      <Typography color="textSecondary">
        No accounts match “{search.trim()}”.
      </Typography>
    );
  } else {
    accountsBody = (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Belongs to</TableCell>
            <TableCell>Team</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Usage (read / write)</TableCell>
            <TableCell>Secret rotated</TableCell>
            <TableCell>Last used</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredItems.map(account => (
            <TableRow
              key={account.accountId}
              hover
              style={{ cursor: 'pointer' }}
              onClick={() => void openAccessLogs(account)}
            >
              <TableCell>
                <Typography variant="body2">
                  <strong>{account.displayName?.trim() || 'Unnamed'}</strong>
                </Typography>
              </TableCell>
              <TableCell>{account.belongsTo || '—'}</TableCell>
              <TableCell>{account.teamName || '—'}</TableCell>
              <TableCell>
                {formatStatus(account.status)}
                {account.secretRotationDue && account.status !== 'revoked'
                  ? ' (rotation due)'
                  : ''}
                {account.hasPendingSecret ? ' · pending secret' : ''}
              </TableCell>
              <TableCell>
                {account.readCount} / {account.writeCount}
              </TableCell>
              <TableCell>{formatWhen(account.secretRotatedAt)}</TableCell>
              <TableCell>{formatWhen(account.lastUsedAt)}</TableCell>
              <TableCell align="right" onClick={e => e.stopPropagation()}>
                {account.status !== 'revoked' && (
                  <>
                    <Button size="small" onClick={() => openEdit(account)}>
                      Edit
                    </Button>
                    {isSuspended(account.status) && (
                      <Button
                        size="small"
                        color="primary"
                        onClick={() =>
                          setConfirmAction({ kind: 'reinstate', account })
                        }
                      >
                        Reinstate
                      </Button>
                    )}
                    {account.hasPendingSecret && (
                      <Button
                        size="small"
                        onClick={() => void onRevealPending(account)}
                      >
                        Reveal secret
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={() =>
                        setConfirmAction({ kind: 'rotate', account })
                      }
                    >
                      Rotate secret
                    </Button>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() =>
                        setConfirmAction({ kind: 'revoke', account })
                      }
                    >
                      Revoke
                    </Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  let auditBody: JSX.Element;
  if (auditLoading) {
    auditBody = <Progress />;
  } else if (auditLogs.length === 0 && !auditError) {
    auditBody = (
      <Typography color="textSecondary">
        No API access logged yet for this service account.
      </Typography>
    );
  } else {
    auditBody = (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Method</TableCell>
            <TableCell>Path</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell>Write type</TableCell>
            <TableCell>Bytes written</TableCell>
            <TableCell>Outcome</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {auditLogs.map(entry => (
            <TableRow key={entry.id}>
              <TableCell>{formatWhen(entry.accessedAt)}</TableCell>
              <TableCell>{entry.method}</TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  style={{
                    maxWidth: 360,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={entry.path}
                >
                  {entry.path}
                </Typography>
              </TableCell>
              <TableCell>{entry.kind}</TableCell>
              <TableCell>{formatWriteType(entry)}</TableCell>
              <TableCell>
                {entry.kind === 'write'
                  ? formatBytes(entry.bytesWritten)
                  : '—'}
              </TableCell>
              <TableCell>
                {entry.outcome}
                {entry.statusCode !== null && entry.statusCode !== undefined
                  ? ` (${entry.statusCode})`
                  : ''}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  let confirmTitle = 'Rotate secret';
  let confirmBody = '';
  let confirmButton = 'Rotate';
  if (confirmAction?.kind === 'revoke') {
    confirmTitle = 'Revoke service account';
    confirmBody = `Revoke service account id "${confirmAction.account.id}"? Existing CLI tokens stop working immediately.`;
    confirmButton = 'Revoke';
  } else if (confirmAction?.kind === 'reinstate') {
    confirmTitle = 'Reinstate service account';
    confirmBody = `Reinstate service account id "${confirmAction.account.id}"? It was suspended for overwhelming the database; API activity will be allowed again under its permission matrix and rate limits.`;
    confirmButton = 'Reinstate';
  } else if (confirmAction) {
    confirmBody = `Rotate secret for id "${confirmAction.account.id}"? A new secret will be shown once and current CLI sessions are revoked.`;
    if (isSuspended(confirmAction.account.status)) {
      confirmBody +=
        ' This also clears suspension and reactivates the account.';
    }
  }

  return (
    <Page themeId="tool">
      <Header
        title="Service Accounts"
        subtitle="Auto-generated id + secret for CLI automation"
      />
      <Content>
        <InfoCard
          title="Accounts"
          action={
            <Button
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
            >
              Add
            </Button>
          }
        >
          {error && (
            <Typography color="error" paragraph>
              {error}
            </Typography>
          )}
          {!loading && items.length > 0 && (
            <Box mb={2} maxWidth={420}>
              <TextField
                label="Search by name or id"
                value={search}
                onChange={e => setSearch(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="e.g. dheeraj89 or sa_…"
              />
            </Box>
          )}
          {accountsBody}
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              Click a row for id, ownership, permissions, and access logs. Secrets are shown
              only once (create/rotate, or Reveal after auto-rotation). API access is limited
              by the permission matrix. Flooding / overwhelming the database{' '}
              <strong>suspends</strong> the account until an admin reinstates it. CLI:{' '}
              <code>iron-phoenix auth login --service-account --id …</code>
            </Typography>
          </Box>
        </InfoCard>

        <Dialog
          open={addOpen}
          onClose={() => !saving && setAddOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Add service account</DialogTitle>
          <DialogContent>
            <Typography paragraph>
              Id and secret are generated automatically. Belongs to must be a GitLab
              username that already exists in Iron Phoenix. Grant only the API permissions
              this account needs.
            </Typography>
            <TextField
              label="Display name (optional)"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              fullWidth
              margin="dense"
              placeholder="e.g. CI pipeline — staging"
            />
            <TextField
              label="Belongs to (GitLab username)"
              value={belongsTo}
              onChange={e => setBelongsTo(e.target.value)}
              fullWidth
              required
              margin="dense"
              placeholder="e.g. jdoe"
              error={
                belongsTo.trim().length > 0 &&
                (belongsToCheck === 'missing' || belongsToCheck === 'error')
              }
              helperText={belongsToHelper(belongsToCheck)}
            />
            <TextField
              label="Team name"
              value={teamName}
              onChange={e => setTeamName(e.target.value)}
              fullWidth
              required
              margin="dense"
              placeholder="e.g. Platform Engineering"
            />
            <Box mt={2} mb={1}>
              <Typography variant="subtitle2">Permissions</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Read + Users allows listing/reading catalog users only. Unchecked cells deny
                access.
              </Typography>
              <PermissionMatrix value={permissions} onChange={setPermissions} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setAddOpen(false);
                resetCreateForm();
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => void onCreate()}
              disabled={
                saving ||
                !belongsTo.trim() ||
                !teamName.trim() ||
                belongsToCheck !== 'found'
              }
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(editAccount)}
          onClose={() => !saving && setEditAccount(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            Edit service account
            {editAccount?.displayName ? ` — ${editAccount.displayName}` : ''}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Display name (optional)"
              value={editDisplayName}
              onChange={e => setEditDisplayName(e.target.value)}
              fullWidth
              margin="dense"
            />
            <TextField
              label="Belongs to (GitLab username)"
              value={editBelongsTo}
              onChange={e => setEditBelongsTo(e.target.value)}
              fullWidth
              required
              margin="dense"
              error={
                editBelongsTo.trim().length > 0 &&
                (editBelongsToCheck === 'missing' ||
                  editBelongsToCheck === 'error')
              }
              helperText={belongsToHelper(editBelongsToCheck)}
            />
            <TextField
              label="Team name"
              value={editTeamName}
              onChange={e => setEditTeamName(e.target.value)}
              fullWidth
              required
              margin="dense"
            />
            <Box mt={2} mb={1}>
              <Typography variant="subtitle2">Permissions</Typography>
              <PermissionMatrix
                value={editPermissions}
                onChange={setEditPermissions}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditAccount(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => void onSaveEdit()}
              disabled={
                saving ||
                !editBelongsTo.trim() ||
                !editTeamName.trim() ||
                editBelongsToCheck !== 'found'
              }
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(revealed)}
          onClose={() => setRevealed(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{revealed?.title}</DialogTitle>
          <DialogContent>
            <Typography paragraph>
              Copy the secret now — this is the only time it is shown. After you close
              this dialog it cannot be retrieved again (rotate to generate a new one).
            </Typography>
            <TextField
              label="Id"
              fullWidth
              variant="outlined"
              value={revealed?.id || ''}
              InputProps={{ readOnly: true }}
              margin="dense"
            />
            <TextField
              label="Secret (one-time)"
              fullWidth
              multiline
              variant="outlined"
              value={revealed?.secret || ''}
              InputProps={{ readOnly: true }}
              margin="dense"
            />
          </DialogContent>
          <DialogActions>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                if (revealed) {
                  void navigator.clipboard.writeText(
                    `ID=${revealed.id}\nSECRET=${revealed.secret}`,
                  );
                }
              }}
            >
              Copy both
            </Button>
            <Button onClick={() => setRevealed(null)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(detailAccount)}
          onClose={() => setDetailAccount(null)}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Service account details
            {detailAccount?.displayName ? ` — ${detailAccount.displayName}` : ''}
          </DialogTitle>
          <DialogContent>
            {detailAccount && (
              <Box mb={2}>
                <TextField
                  label="Id"
                  fullWidth
                  variant="outlined"
                  value={detailAccount.id}
                  InputProps={{ readOnly: true }}
                  margin="dense"
                  helperText="Id is always visible. The secret is never shown here."
                />
                <TextField
                  label="Secret"
                  fullWidth
                  variant="outlined"
                  value="••••••••••••••••"
                  InputProps={{ readOnly: true }}
                  margin="dense"
                  helperText={
                    detailAccount.hasPendingSecret
                      ? 'A new secret is waiting — use Reveal secret once, then it is gone forever.'
                      : 'Secret is hidden. It can only be shown once (on create, rotate, or after auto-rotation).'
                  }
                />
                <Typography variant="body2" color="textSecondary" paragraph>
                  Belongs to: {detailAccount.belongsTo || '—'} · Team:{' '}
                  {detailAccount.teamName || '—'}
                </Typography>
                {isSuspended(detailAccount.status) && (
                  <Typography color="error" paragraph>
                    This account is suspended for overwhelming the database with excessive
                    API requests. All activity is halted until an admin reinstates it.
                  </Typography>
                )}
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Permissions
                  </Typography>
                  <PermissionMatrix
                    value={normalizePermissions(detailAccount.permissions)}
                    onChange={() => undefined}
                    disabled
                  />
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  style={{ gap: 8 }}
                  mb={1}
                >
                  <Typography variant="body2" color="textSecondary">
                    Status: {formatStatus(detailAccount.status)} · Usage:{' '}
                    {detailAccount.readCount} reads / {detailAccount.writeCount}{' '}
                    writes · Last used: {formatWhen(detailAccount.lastUsedAt)}
                  </Typography>
                  {detailAccount.status !== 'revoked' && (
                    <Box>
                      <Button
                        size="small"
                        onClick={() => {
                          const account = detailAccount;
                          setDetailAccount(null);
                          openEdit(account);
                        }}
                      >
                        Edit
                      </Button>
                      {isSuspended(detailAccount.status) && (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() => {
                            setConfirmAction({
                              kind: 'reinstate',
                              account: detailAccount,
                            });
                          }}
                        >
                          Reinstate
                        </Button>
                      )}
                      {detailAccount.hasPendingSecret && (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() => {
                            const account = detailAccount;
                            setDetailAccount(null);
                            void onRevealPending(account);
                          }}
                        >
                          Reveal secret once
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Access logs
            </Typography>
            {auditError && (
              <Typography color="error" paragraph>
                {auditError}
              </Typography>
            )}
            {auditBody}
          </DialogContent>
          <DialogActions>
            {detailAccount && (
              <Button
                onClick={() => void openAccessLogs(detailAccount)}
                disabled={auditLoading}
              >
                Refresh logs
              </Button>
            )}
            {detailAccount && (
              <Button
                onClick={() => {
                  void navigator.clipboard.writeText(detailAccount.id);
                }}
              >
                Copy id
              </Button>
            )}
            <Button onClick={() => setDetailAccount(null)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(confirmAction)}
          onClose={() => setConfirmAction(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{confirmTitle}</DialogTitle>
          <DialogContent>
            <Typography>{confirmBody}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button
              color={confirmAction?.kind === 'revoke' ? 'secondary' : 'primary'}
              variant="contained"
              onClick={() => void onConfirmAction()}
            >
              {confirmButton}
            </Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
};
